import { motion } from "framer-motion";
import { HOW_IT_WORKS } from "@/constants/testIds";
import { Search, Compass, PenTool, Radar, Coins } from "lucide-react";

const STEPS = [
    {
        n: 1,
        icon: Search,
        title: "Research",
        copy: "We dig into your brand, audience, competitors, and goals. The entire landscape you're operating in.",
    },
    {
        n: 2,
        icon: Compass,
        title: "Strategy",
        copy: "A growth roadmap built around your business, not a template. Every play is chosen for a reason.",
    },
    {
        n: 3,
        icon: PenTool,
        title: "Content & Profile Optimisation",
        copy: "Hooks, captions, SEO, and posting strategy rebuilt to convert, not just to fill your feed.",
    },
    {
        n: 4,
        icon: Radar,
        title: "Audience Domination",
        copy: "Getting your content in front of the people who actually buy. Organically, at scale, week after week.",
    },
    {
        n: 5,
        icon: Coins,
        title: "Conversion",
        copy: "Turning reach into enquiries, and enquiries into sales. Profile funnel, DMs, and offers all pull their weight.",
    },
];

export default function Process() {
    return (
        <section
            id="how-it-works"
            data-testid={HOW_IT_WORKS.root}
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-5">
                        Our Process
                    </p>
                    <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[var(--text-primary)]">
                        Five steps to{" "}
                        <span className="italic text-[var(--accent-champagne)]">
                            complete market ownership.
                        </span>
                    </h2>
                </div>

                <div className="mt-16 md:mt-24 relative">
                    <div
                        className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-[var(--accent-gold)] to-transparent -translate-x-1/2"
                        aria-hidden
                    />

                    <div className="space-y-16 md:space-y-24">
                        {STEPS.map((step, i) => {
                            const Icon = step.icon;
                            const alignRight = i % 2 === 1;
                            return (
                                <motion.div
                                    key={step.n}
                                    data-testid={HOW_IT_WORKS.step(step.n)}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.7 }}
                                    className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center"
                                >
                                    <div className={alignRight ? "md:order-2 md:text-right" : ""}>
                                        <div
                                            className={`inline-flex items-center gap-3 mb-4 ${
                                                alignRight ? "md:flex-row-reverse" : ""
                                            }`}
                                        >
                                            <span className="font-fraktur text-[var(--accent-gold)] text-2xl">
                                                {String(step.n).padStart(2, "0")}
                                            </span>
                                            <span className="font-fraktur text-[var(--accent-champagne)]/60 text-lg tracking-[0.2em]">
                                                step
                                            </span>
                                        </div>
                                        <h3 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-[var(--text-primary)] leading-tight mb-4">
                                            {step.title}
                                        </h3>
                                        <p className="text-lg text-[var(--text-secondary)] leading-relaxed max-w-md md:inline-block">
                                            {step.copy}
                                        </p>
                                    </div>

                                    <div
                                        className={`relative flex justify-center ${
                                            alignRight ? "md:order-1" : ""
                                        }`}
                                    >
                                        <div className="relative w-40 h-40 md:w-56 md:h-56">
                                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-dark)] opacity-40 blur-2xl" />
                                            <div className="relative w-full h-full rounded-full surface-3d flex items-center justify-center border border-[var(--border-accent)]">
                                                <Icon
                                                    size={44}
                                                    className="text-[var(--accent-gold)]"
                                                    strokeWidth={1.5}
                                                />
                                                <span className="absolute -top-3 -right-3 w-12 h-12 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-bold font-display text-2xl flex items-center justify-center shadow-[0_10px_25px_-5px_rgba(212,175,55,0.7)]">
                                                    {step.n}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
